import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import handleError from "../../../constants/handleError";
import InvoiceTemp from "../estimation/InvoiceTemp";
import DeleteModal from "../../../constants/DeleteModal";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import { useLocation, useNavigate } from "react-router-dom";
import Table from "../../../constants/Table";
import { DeleteIcon, EditIcon, ShowIcon } from "../../../constants/Icons";

const EstimateList = ({ customerId }) => {
  const { isUser } = useAuth();
  const hasFetched = useRef(false);
  const [isEstimateData, setEstimateData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [currentEstimatePage, setCurrentEstimatePage] = useState(0);
  const [totalEstimatePages, setTotalEstimatePages] = useState(0);
  const [totalEstimateItems, setTotalEstimateItems] = useState(0);
  const { permission, hasPermission } = usePermission();

  const perPage = 10;
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const navigate = useNavigate();
  const location = useLocation();

  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleListEstimate = async (customerId, page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const response = await AxiosInstance.get(
        `module/estimate/customer/${customerId}?pageno=${
          page + 1
        }&limit=${perPage}`,
      );
      const results = response.data.results || [];
      setEstimateData(results);
      if (results.length === 0) {
        setTotalEstimatePages(0);
        setTotalEstimateItems(0);
        setCurrentEstimatePage(0);
      } else {
        setTotalEstimatePages(response.data.totalpages || 0);
        setTotalEstimateItems(response.data.totalitems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (item) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:view") ||
      permission === undefined
    ) {
      try {
        const response = await AxiosInstance.get("module/storesettings");
        const fullData = {
          ...item,
          settings: response.data.results.paymentSettings,
        };
        setViewData(fullData);
        setViewModalOpen(true);
      } catch (error) {
        console.error("Error fetching setting data:", error);
        showAlert("error", "Failed to fetch setting data");
      }
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
    // Clear the location state to prevent re-opening the modal
    navigate(location.pathname, { replace: true, state: {} });
  };

  const handleEdit = (item) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:updateItem") ||
      permission === undefined
    ) {
      const navigatelist = "/listestimation";
      navigate(`/editestimate`, { state: { item, navigatelist } });
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const openDeleteModal = (cItem) => {
    if (
      hasPermission("ESTIMATE", "ESTIMATE:delete") ||
      permission === undefined
    ) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleDone = () => {
    setViewModalOpen(false);
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.delete(
        `module/estimate/${isDeleteData?._id}`,
      );
      showAlert("success", Messages.SUCCESS.ESTIMATE_DELETED);

      const newTotal = totalEstimateItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isEstimateData.length === 1 && currentEstimatePage > 0) {
        setCurrentEstimatePage((prev) => prev - 1);
        await handleListEstimate(customerId, currentEstimatePage - 1);
      } else {
        await handleListEstimate(customerId, currentEstimatePage);
      }

      setTotalEstimateItems(newTotal);
      setTotalEstimatePages(newTotalPages);
      setDeleteModalOpen(false);

      closeDeleteModal();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteData(null);
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (customerId) {
      handleListEstimate(customerId, currentEstimatePage);
    }
  }, [customerId]);

  return (
    <div>
      <Table
        loading={isLoading}
        data={isEstimateData}
        onRowClick={(item) => handleView(item)}
        columns={[
          {
            label: "S.No",
            render: (_, index) => currentEstimatePage * perPage + index + 1,
          },
          {
            label: "Estimate No",
            render: (item) => (
              <span className="badge rounded-pill bg-label-deep-purple">
                {item?.estimateNo}
              </span>
            ),
          },
          {
            label: "Estimate Date",
            render: (item) => (
              <span className="text-heading fw-medium text-truncate">
                {
                  DateConverter.ISOToDisplayFormat(
                    DateConverter.convertToTimeZone(
                      item?.estimateDate,
                      isUser?.timezone,
                    ),
                  ).date
                }
              </span>
            ),
          },
          {
            label: "Estimate To",
            render: (item) => (
              <div className="d-flex flex-column">
                <span className="text-nowrap text-heading fw-medium">
                  {item.estimateTo ? `${item.estimateTo.name}` : ""}
                </span>
                <span className="text-nowrap">
                  <small>
                    {item.estimateTo ? `+91 ${item.estimateTo.phone}` : " - "}
                  </small>
                </span>
              </div>
            ),
          },
          {
            label: "Estimate Items",
            render: (item) => <>{item?.estimateItems?.length || 0} Item(s)</>,
          },
          {
            label: "Grand Total",
            render: (item) => (
              <span className="text-heading fw-medium text-truncate">
                ₹
                {CurrencyConverter.toCurrency(
                  Number(item.grandTotal) || 0,
                  "INR",
                )}
              </span>
            ),
          },
          {
            label: "Actions",
            render: (item) => (
              <div className="order-actions opacity-0 opacity-md-100">
                <button
                  type="button"
                  className="btn-label-info shadow-none me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(item);
                  }}
                >
                  <ShowIcon/>
                </button>
                {item?.status !== "ACTIVE" && (
                  <>
                    <button
                      type="button"
                      className="btn-label-primary shadow-none me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                      }}
                    >
                      <EditIcon/>
                    </button>
                    <button
                      type="button"
                      className="btn-label-danger shadow-none me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(item);
                      }}
                    >
                      <DeleteIcon/>
                    </button>
                  </>
                )}
              </div>
            ),
          },
        ].filter(Boolean)}
      />
      {totalEstimatePages > 0 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            previousLabel={"← Previous"}
            nextLabel={"Next →"}
            breakLabel={"..."}
            onPageChange={({ selected }) => {
              setIsLoading(true);
              setCurrentEstimatePage(selected);
            }}
            pageRangeDisplayed={5}
            pageCount={totalEstimatePages}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            forcePage={currentEstimatePage}
          />
        </div>
      )}
      {isViewModalOpen && (
        <InvoiceTemp
          data={isViewData}
          onClose={handleClose}
          onEdit={handleEdit}
          onDone={handleDone}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteModal
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDelete}
          isLable={" Estimation " + isDeleteData?.estimateno}
          isBtnLoading={isBtnLoading}
        />
      )}
    </div>
  );
};

export default EstimateList;
